import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Slider,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import type { CopMapLayerPrefs } from '../utils/copMapLayerPrefs';
import type {
  CameraLayerDisplay,
  CameraMarkerSize,
} from '@signals/app-shell/hooks/useCameraLocationsOverlay';
import {
  CameraLayerFilterPanel,
  CameraProximityRow,
  type CameraLayerFilterOverlay,
  type CameraProximityControl,
} from '@signals/app-shell/components/CameraLayerFilterPopover';
import ReferenceLayersSection from '@signals/app-shell/components/map/ReferenceLayersSection';
import type { ReferenceLayersController } from '@signals/app-shell/hooks/useReferenceLayers';

/** Overlay toggles that used to live on the toolbar; the dialog is now their only home. */
export type CopOverlayToggles = {
  traffic: boolean;
  onTraffic: (v: boolean) => void;
  incidents: boolean;
  onIncidents: (v: boolean) => void;
  incidentsBusy?: boolean;
  weatherRadar: boolean;
  onWeatherRadar: (v: boolean) => void;
  weatherRadarOpacity: number;
  onWeatherRadarOpacity: (v: number) => void;
  dfpHeatmap: boolean;
  onDfpHeatmap: (v: boolean) => void;
  dfpHeatmapCount?: number;
  /** False when the RF Signal module is switched off — the row is hidden. */
  dfpHeatmapAvailable?: boolean;
  itinerary: boolean;
  onItinerary: (v: boolean) => void;
  itineraryAvailable: boolean;
  /** Per-user itinerary time window (look-back / look-ahead), saved by the parent. */
  itineraryWindow?: {
    lookbackMinutes: number;
    lookaheadHours: number;
    onChange?: (lookbackMinutes: number, lookaheadHours: number) => void;
    /** Itinerary points currently inside the window. */
    pointCount?: number;
  };
  cameras: boolean;
  onCameras: (v: boolean) => void;
};

type Props = {
  open: boolean;
  onClose: () => void;
  prefs: CopMapLayerPrefs;
  onPrefsChange: (patch: Partial<CopMapLayerPrefs>) => void;
  onReset: () => void;
  isDefault: boolean;
  overlays: CopOverlayToggles;
  cameraDisplay: CameraLayerDisplay;
  onCameraDisplayChange: (patch: Partial<CameraLayerDisplay>) => void;
  /** Category / source / near-entities filters, embedded under CAMERAS. */
  cameraFilters?: { overlay: CameraLayerFilterOverlay; proximity?: CameraProximityControl; shownCount: number };
  /** Admin-uploaded boundaries / districts / POIs etc. */
  referenceLayers?: ReferenceLayersController;
  onZoomToBounds?: (bbox: [number, number, number, number]) => void;
  isAdmin?: boolean;
  /** Counts shown next to section titles (optional). */
  counts?: { devices?: number; dwells?: number; geofences?: number; cameras?: number; alerts?: number };
  /**
   * True when the map can draw drone camera footprint / area-observed wedges;
   * shows the two rows under DEVICES. Omit on deployments without drone feeds.
   */
  deviceCoverage?: boolean;
};

type RowProps = {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  hint?: string;
};

const Row: React.FC<RowProps> = ({ label, checked, onChange, disabled, hint }) => {
  const control = (
    <FormControlLabel
      sx={{ m: 0 }}
      control={<Switch size="small" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />}
      label={<Typography variant="caption">{label}</Typography>}
    />
  );
  return hint ? (
    <Tooltip title={hint} placement="top" arrow>
      <span>{control}</span>
    </Tooltip>
  ) : (
    control
  );
};

const ITINERARY_DEFAULT_LOOKBACK_MIN = 120;
const ITINERARY_DEFAULT_LOOKAHEAD_H = 6;

/**
 * Inline look-back / look-ahead editor for itinerary points. Drafts are local;
 * a value commits on blur / Enter so typing doesn't thrash the parent prefs.
 */
const ItineraryWindowFields: React.FC<{
  lookbackMinutes: number;
  lookaheadHours: number;
  onChange?: (lookbackMinutes: number, lookaheadHours: number) => void;
  pointCount?: number;
  enabled: boolean;
}> = ({ lookbackMinutes, lookaheadHours, onChange, pointCount, enabled }) => {
  const [lookback, setLookback] = useState(String(lookbackMinutes));
  const [lookahead, setLookahead] = useState(String(lookaheadHours));
  useEffect(() => setLookback(String(lookbackMinutes)), [lookbackMinutes]);
  useEffect(() => setLookahead(String(lookaheadHours)), [lookaheadHours]);

  const commit = (nextLookback: string, nextLookahead: string) => {
    const lb = Math.max(0, Math.min(24 * 60, Math.round(Number(nextLookback))));
    const la = Math.max(0, Math.min(7 * 24, Math.round(Number(nextLookahead) * 10) / 10));
    if (!Number.isFinite(lb) || !Number.isFinite(la)) {
      setLookback(String(lookbackMinutes));
      setLookahead(String(lookaheadHours));
      return;
    }
    setLookback(String(lb));
    setLookahead(String(la));
    if (lb !== lookbackMinutes || la !== lookaheadHours) onChange?.(lb, la);
  };
  const onKey = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') (e.target as HTMLElement).blur();
  };
  const isDefault = lookbackMinutes === ITINERARY_DEFAULT_LOOKBACK_MIN && lookaheadHours === ITINERARY_DEFAULT_LOOKAHEAD_H;

  return (
    <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, pl: 0.5, mt: 0.25 }}>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        Itinerary window
      </Typography>
      <TextField
        size="small"
        type="number"
        label="Past (min)"
        value={lookback}
        disabled={!onChange}
        onChange={(e) => setLookback(e.target.value)}
        onBlur={() => commit(lookback, lookahead)}
        onKeyDown={onKey}
        inputProps={{ min: 0, max: 1440, step: 5, 'aria-label': 'Itinerary look-back minutes' }}
        sx={{ width: 96, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
      />
      <TextField
        size="small"
        type="number"
        label="Future (h)"
        value={lookahead}
        disabled={!onChange}
        onChange={(e) => setLookahead(e.target.value)}
        onBlur={() => commit(lookback, lookahead)}
        onKeyDown={onKey}
        inputProps={{ min: 0, max: 168, step: 0.5, 'aria-label': 'Itinerary look-ahead hours' }}
        sx={{ width: 96, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
      />
      <Button
        size="small"
        disabled={!onChange || isDefault}
        onClick={() => commit(String(ITINERARY_DEFAULT_LOOKBACK_MIN), String(ITINERARY_DEFAULT_LOOKAHEAD_H))}
        sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0, minWidth: 0 }}
      >
        Defaults
      </Button>
      <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
        {enabled && pointCount != null
          ? `${pointCount.toLocaleString()} point${pointCount === 1 ? '' : 's'} in window`
          : 'Saved for your account on this browser'}
      </Typography>
    </Box>
  );
};

const Section: React.FC<{ title: string; count?: number; children: React.ReactNode }> = ({ title, count, children }) => (
  <Box sx={{ mb: 1.25 }}>
    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4, display: 'block', mb: 0.5 }}>
      {title}
      {count != null && count > 0 && (
        <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
          ({count.toLocaleString()})
        </Typography>
      )}
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
        columnGap: 1,
        rowGap: 0.25,
      }}
    >
      {children}
    </Box>
  </Box>
);

/**
 * Centered dialog owning the COP map's layer visibility, clustering and label
 * switches — the COP counterpart to Pathfinder's MapLayerControlsDialog.
 * Floats over the map so nothing resizes when it opens.
 */
const COPMapLayersDialog: React.FC<Props> = ({
  open,
  onClose,
  prefs,
  onPrefsChange,
  onReset,
  isDefault,
  overlays,
  cameraDisplay,
  onCameraDisplayChange,
  cameraFilters,
  referenceLayers,
  onZoomToBounds,
  isAdmin,
  counts,
  deviceCoverage,
}) => {
  const [cameraFiltersOpen, setCameraFiltersOpen] = useState(false);
  const cameraFiltersActive = Boolean(
    cameraFilters && (cameraFilters.overlay.hasActiveFilters || cameraFilters.proximity?.prefs.enabled)
  );
  return (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { borderRadius: 2 } }}
    aria-labelledby="cop-map-layers-title"
  >
    <DialogTitle id="cop-map-layers-title" sx={{ display: 'flex', alignItems: 'center', py: 1.25, pr: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
        Map layers
      </Typography>
      <Tooltip title="Reset to defaults">
        <span>
          <IconButton size="small" onClick={onReset} disabled={isDefault} aria-label="Reset map layers">
            <ResetIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <IconButton size="small" onClick={onClose} aria-label="Close">
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ pt: 0.5, pb: 1.5 }}>
      <Section title="DEVICES" count={counts?.devices}>
        <Row label="Pins" checked={prefs.devices} onChange={(v) => onPrefsChange({ devices: v })} />
        <Row
          label="Labels"
          checked={prefs.deviceLabels}
          disabled={!prefs.devices}
          onChange={(v) => onPrefsChange({ deviceLabels: v })}
          hint="Entity designator drawn inside each pin"
        />
        <Row
          label="Cluster"
          checked={prefs.clusterDevices}
          disabled={!prefs.devices}
          onChange={(v) => onPrefsChange({ clusterDevices: v })}
          hint="Group nearby pins into count bubbles until zoomed in"
        />
        {deviceCoverage && (
          <>
            <Row
              label="Camera footprint"
              checked={prefs.cameraFootprint}
              disabled={!prefs.devices}
              onChange={(v) => onPrefsChange({ cameraFootprint: v })}
              hint="Current field-of-view wedge for drone cameras"
            />
            <Row
              label="Area observed"
              checked={prefs.areaObserved}
              disabled={!prefs.devices}
              onChange={(v) => onPrefsChange({ areaObserved: v })}
              hint="Accumulated ground a drone camera has covered"
            />
          </>
        )}
      </Section>

      <Divider sx={{ my: 1 }} />

      <Section title="ALERTS" count={counts?.alerts}>
        <Row
          label="Pins"
          checked={prefs.alerts}
          onChange={(v) => onPrefsChange({ alerts: v })}
          hint="DFP, watchlist, co-traveler and mission threat markers"
        />
      </Section>

      <Divider sx={{ my: 1 }} />

      <Section title="CAMERAS" count={counts?.cameras}>
        <Row label="Cameras" checked={overlays.cameras} onChange={overlays.onCameras} />
        <Row
          label="Cluster"
          checked={cameraDisplay.cluster}
          disabled={!overlays.cameras}
          onChange={(v) => onCameraDisplayChange({ cluster: v })}
        />
        <Row
          label="Labels"
          checked={cameraDisplay.labels}
          disabled={!overlays.cameras}
          onChange={(v) => onCameraDisplayChange({ labels: v })}
          hint="Camera names under markers from zoom 13 (clusters open up earlier while this is on)"
        />
        <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
          <Typography variant="caption" color="text.secondary">
            Marker size
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={cameraDisplay.size}
            disabled={!overlays.cameras}
            onChange={(_, v: CameraMarkerSize | null) => v && onCameraDisplayChange({ size: v })}
            sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.25, fontSize: '0.65rem', textTransform: 'none' } }}
            aria-label="Camera marker size"
          >
            <ToggleButton value="small">Small</ToggleButton>
            <ToggleButton value="medium">Medium</ToggleButton>
            <ToggleButton value="large">Large</ToggleButton>
          </ToggleButtonGroup>
          {cameraFilters && (
            <Button
              size="small"
              onClick={() => setCameraFiltersOpen((v) => !v)}
              disabled={!overlays.cameras}
              endIcon={cameraFiltersOpen ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
              sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.7rem', py: 0 }}
              aria-expanded={cameraFiltersOpen}
            >
              Filters
              {overlays.cameras && (
                <Typography component="span" variant="caption" color={cameraFiltersActive ? 'primary.main' : 'text.disabled'} sx={{ ml: 0.5 }}>
                  {cameraFilters.shownCount.toLocaleString()} / {cameraFilters.overlay.totalCount.toLocaleString()}
                </Typography>
              )}
            </Button>
          )}
        </Box>
        {cameraFilters?.proximity && (
          <Box sx={{ gridColumn: '1 / -1', mt: 0.5 }}>
            <CameraProximityRow proximity={cameraFilters.proximity} disabled={!overlays.cameras} />
          </Box>
        )}
        {cameraFilters && (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Collapse in={cameraFiltersOpen && overlays.cameras} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 0.75, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                <CameraLayerFilterPanel overlay={cameraFilters.overlay} hideHeader hideDisplay />
              </Box>
            </Collapse>
          </Box>
        )}
      </Section>

      <Divider sx={{ my: 1 }} />

      <Section title="DWELLS" count={counts?.dwells}>
        <Row label="Circles" checked={prefs.dwellCircles} onChange={(v) => onPrefsChange({ dwellCircles: v })} />
        <Row
          label="Labels"
          checked={prefs.dwellLabels}
          onChange={(v) => onPrefsChange({ dwellLabels: v })}
          hint="Center dot + duration text"
        />
      </Section>

      <Divider sx={{ my: 1 }} />

      <Section title="GEOFENCES" count={counts?.geofences}>
        <Row label="Polygons" checked={prefs.geofences} onChange={(v) => onPrefsChange({ geofences: v })} />
        <Row
          label="Labels"
          checked={prefs.geofenceLabels}
          disabled={!prefs.geofences}
          onChange={(v) => onPrefsChange({ geofenceLabels: v })}
        />
      </Section>

      <Divider sx={{ my: 1 }} />

      <Section title="OVERLAYS">
        <Row
          label="Itinerary"
          checked={overlays.itinerary}
          disabled={!overlays.itineraryAvailable}
          onChange={overlays.onItinerary}
          hint={overlays.itineraryAvailable ? undefined : 'Pin an itinerary subject first'}
        />
        <Row
          label="Itinerary labels"
          checked={prefs.itineraryLabels}
          disabled={!overlays.itinerary}
          onChange={(v) => onPrefsChange({ itineraryLabels: v })}
        />
        {overlays.itineraryWindow && (
          <ItineraryWindowFields
            lookbackMinutes={overlays.itineraryWindow.lookbackMinutes}
            lookaheadHours={overlays.itineraryWindow.lookaheadHours}
            onChange={overlays.itineraryWindow.onChange}
            pointCount={overlays.itineraryWindow.pointCount}
            enabled={overlays.itinerary}
          />
        )}
        <Row label="Traffic" checked={overlays.traffic} onChange={overlays.onTraffic} hint="Live congestion on roads" />
        <Row
          label="Incidents"
          checked={overlays.incidents}
          disabled={overlays.incidentsBusy}
          onChange={overlays.onIncidents}
          hint="Traffic incidents and weather alerts in the current area"
        />
        {overlays.dfpHeatmapAvailable !== false && (
          <Row
            label={overlays.dfpHeatmapCount != null ? `DFP heatmap (${overlays.dfpHeatmapCount})` : 'DFP heatmap'}
            checked={overlays.dfpHeatmap}
            onChange={overlays.onDfpHeatmap}
            hint="Density of DFP alerts"
          />
        )}
        <Row label="Weather radar" checked={overlays.weatherRadar} onChange={overlays.onWeatherRadar} hint="NOAA base reflectivity" />
        <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 1.5, pl: 0.5, pr: 1 }}>
          <Typography variant="caption" color={overlays.weatherRadar ? 'text.secondary' : 'text.disabled'} sx={{ whiteSpace: 'nowrap' }}>
            Radar opacity
          </Typography>
          <Slider
            size="small"
            aria-label="Weather radar opacity"
            min={0.1}
            max={0.9}
            step={0.05}
            disabled={!overlays.weatherRadar}
            value={overlays.weatherRadarOpacity}
            onChange={(_, v) => overlays.onWeatherRadarOpacity(Array.isArray(v) ? v[0] : v)}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
            sx={{ flex: 1, color: '#03a9f4' }}
          />
          <Typography variant="caption" color={overlays.weatherRadar ? 'text.secondary' : 'text.disabled'} sx={{ width: 32, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(overlays.weatherRadarOpacity * 100)}%
          </Typography>
        </Box>
      </Section>

      {referenceLayers && (
        <>
          <Divider sx={{ my: 1 }} />
          <ReferenceLayersSection controller={referenceLayers} onZoomTo={onZoomToBounds} isAdmin={isAdmin} />
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
        <Button size="small" onClick={onClose}>
          Done
        </Button>
      </Box>
    </DialogContent>
  </Dialog>
  );
};

export default COPMapLayersDialog;
